var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * React-query hook for the vocabulary usage report (CZNA-804).
 *
 * The endpoint answers 202 while a background pass runs and 200 once the result is
 * held; `fetchVocabularyUsage` resolves to `null` for 202 and this hook polls until
 * it flips — the same policy as `useFormAnalysis`, including its two terminal
 * states besides success: a failed query (CZNA-519: `data` is `undefined` both
 * while pending and after an error, so the interval must check the status) and a
 * wait that ran out (CZNA-782).
 *
 * Closure-captures `ky`: `useOkapiKy` returns an unstable instance that must not go
 * into dependency arrays.
 */
import { useCallback, useEffect, useRef, useState, } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchVocabularyUsage, refreshVocabularyUsage, } from '../api/vocabularyUsageService';
/** A poll that finds the work unfinished costs the backend a map lookup. */
export var VOCABULARY_USAGE_POLL_MS = 2000;
/**
 * How long to keep asking before saying the report has not answered. Stopping the
 * poll does not stop the job — the backend holds the result once it lands, so
 * "check again" picks it up with one request.
 */
export var VOCABULARY_USAGE_MAX_WAIT_MS = 5 * 60000;
export var useVocabularyUsage = function (typeCode) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var queryKey = ['vocabulary-usage', typeCode];
    var _a = useState(false), timedOut = _a[0], setTimedOut = _a[1];
    var query = useQuery(queryKey, function () { return fetchVocabularyUsage(ky, typeCode); }, {
        // The backend's `computedAt` tells the reader how old the result is, not
        // react-query's cache.
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        enabled: !!typeCode,
        refetchInterval: function (data, current) { return (data || current.state.status === 'error' || timedOut ? false : VOCABULARY_USAGE_POLL_MS); },
    });
    var computing = !!typeCode && !query.isError && (query.isLoading || query.data === null);
    useEffect(function () {
        if (!computing || timedOut)
            return undefined;
        var timer = setTimeout(function () { return setTimedOut(true); }, VOCABULARY_USAGE_MAX_WAIT_MS);
        return function () { return clearTimeout(timer); };
    }, [computing, timedOut]);
    useEffect(function () {
        setTimedOut(false);
    }, [typeCode]);
    var checkAgain = useCallback(function () {
        setTimedOut(false);
        query.refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.refetch]);
    // A refresh that failed leaves the previous result on screen, so the screen has to be
    // told — otherwise the old numbers read as the answer to the click (Codex review, round 1).
    var _b = useState(false), refreshFailed = _b[0], setRefreshFailed = _b[1];
    // Only the latest refresh may report. A POST that fails after the reader moved to
    // another vocabulary, or after a newer refresh went through, would otherwise put its
    // banner over an answer it has nothing to do with (Codex review, round 2).
    var refreshGeneration = useRef(0);
    useEffect(function () {
        refreshGeneration.current += 1;
        setRefreshFailed(false);
    }, [typeCode]);
    /**
     * Forces a fresh pass and drops back to the computing state at once. Never rejects:
     * it is a click handler, and a failure is reported through `refreshFailed`.
     */
    var refresh = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var generation, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!typeCode)
                        return [2 /*return*/];
                    refreshGeneration.current += 1;
                    generation = refreshGeneration.current;
                    setTimedOut(false);
                    setRefreshFailed(false);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, refreshVocabularyUsage(ky, typeCode)];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    if (generation === refreshGeneration.current)
                        setRefreshFailed(true);
                    return [2 /*return*/];
                case 4:
                    if (generation !== refreshGeneration.current)
                        return [2 /*return*/];
                    queryClient.setQueryData(queryKey, null);
                    return [4 /*yield*/, queryClient.invalidateQueries(queryKey)];
                case 5:
                    _b.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [typeCode, queryClient]);
    return __assign(__assign({}, query), { computing: computing, refresh: refresh, refreshFailed: refreshFailed, timedOut: timedOut, checkAgain: checkAgain });
};
